import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Modal, EmptyState } from "@/components/pcp";
import { Plus, ArrowUp, ArrowDown, Warning, Pencil, TrashSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { nome: "", unidade: "un", estoque_minimo: 0, saldo: 0 };

export default function Insumos() {
  const [items, setItems] = useState([]);
  const [movs, setMovs] = useState([]);
  const [modal, setModal] = useState(false);
  const [movModal, setMovModal] = useState(null); // insumo obj
  const [form, setForm] = useState(empty);
  const [mov, setMov] = useState({ tipo: "entrada", quantidade: 1, os_id: "", nota_fiscal: "", observacao: "" });
  const [editId, setEditId] = useState(null);
  const [ordens, setOrdens] = useState([]);

  const load = async () => {
    const [{ data: ins }, { data: mv }, { data: os }] = await Promise.all([
      api.get("/insumos"), api.get("/insumos/movimentos"), api.get("/ordens")
    ]);
    setItems(ins); setMovs(mv); setOrdens(os);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, estoque_minimo: parseFloat(form.estoque_minimo) || 0, saldo: parseFloat(form.saldo) || 0 };
      if (editId) await api.put(`/insumos/${editId}`, payload);
      else await api.post("/insumos", payload);
      toast.success("Insumo salvo"); setModal(false); setForm(empty); setEditId(null); load();
    } catch { toast.error("Erro"); }
  };

  const registerMov = async () => {
    if (!movModal || !mov.quantidade) return;
    await api.post("/insumos/movimentos", { ...mov, insumo_id: movModal.id, quantidade: parseFloat(mov.quantidade), os_id: mov.os_id || null });
    toast.success("Movimento registrado");
    setMovModal(null);
    setMov({ tipo: "entrada", quantidade: 1, os_id: "", nota_fiscal: "", observacao: "" });
    load();
  };

  const remove = async (ins) => {
    const reservado = ins.reservado || 0;
    let msg = `Excluir o insumo "${ins.nome}"?`;
    if (reservado > 0) {
      msg += `\n\nATENÇÃO: existem ${reservado} ${ins.unidade} reservados em OS ativas. Composições e itens que referenciam este insumo continuarão existindo, mas o cálculo de materiais ficará sem estoque de referência.`;
    }
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/insumos/${ins.id}`);
      toast.success("Insumo excluído");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "";
      if (status === 401 || status === 403) {
        toast.error("Sem permissão para excluir. É necessário perfil Admin ou Gestor.");
      } else {
        toast.error(`Erro ao excluir insumo${detail ? ": " + detail : ""}${status ? " (HTTP " + status + ")" : ""}`);
      }
    }
  };

  return (
    <>
      <PageHeader
        testid="page-insumos"
        title="Almoxarifado"
        subtitle={`${items.length} insumos cadastrados`}
        actions={<button className="btn btn-primary" onClick={() => { setForm(empty); setEditId(null); setModal(true); }} data-testid="btn-new-insumo"><Plus size={16} weight="bold" /> Novo insumo</button>}
      />
      <div className="page-body">
        {items.length === 0 ? <EmptyState title="Nenhum insumo cadastrado" /> : (
          <div className="pcp-card" style={{ overflowX: "auto", marginBottom: 24 }}>
            <table className="pcp-table">
              <thead><tr><th>Insumo</th><th>Unidade</th><th style={{ textAlign: "right" }}>Saldo</th><th style={{ textAlign: "right" }}>Reservado</th><th style={{ textAlign: "right" }}>Disponível</th><th style={{ textAlign: "right" }}>Estoque mín.</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {items.map((i) => {
                  const baixo = (i.disponivel ?? i.saldo) < i.estoque_minimo;
                  const negativo = (i.disponivel ?? i.saldo) < 0;
                  return (
                    <tr key={i.id}>
                      <td style={{ fontWeight: 600 }}>{i.nome}</td>
                      <td><span className="chip">{i.unidade}</span></td>
                      <td className="font-mono" style={{ textAlign: "right", fontWeight: 600 }}>{i.saldo}</td>
                      <td className="font-mono" style={{ textAlign: "right", color: (i.reservado || 0) > 0 ? "#F59E0B" : "#71717A" }}>{i.reservado || 0}</td>
                      <td className="font-mono" style={{ textAlign: "right", fontWeight: 700, color: negativo ? "#DC2626" : baixo ? "#F59E0B" : "#0A0A0A" }}>{i.disponivel ?? i.saldo}</td>
                      <td className="font-mono" style={{ textAlign: "right", color: "#71717A" }}>{i.estoque_minimo}</td>
                      <td>
                        {negativo ? <span className="badge badge-aberta"><Warning size={12} weight="bold" /> Insuficiente</span> :
                          baixo ? <span className="badge badge-tratamento"><Warning size={12} weight="bold" /> Baixo</span> :
                            <span className="badge badge-encerrada">OK</span>}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setMovModal(i)} data-testid={`btn-mov-${i.nome}`}>Movimentar</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setForm(i); setEditId(i.id); setModal(true); }} title="Editar"><Pencil size={14} weight="bold" /></button>
                        <button className="btn btn-ghost btn-sm" onClick={() => remove(i)} title="Excluir" data-testid={`btn-del-insumo-${i.nome}`}><TrashSimple size={14} weight="bold" color="#DC2626" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {movs.length > 0 && (
          <div className="pcp-card" style={{ padding: 16 }}>
            <div className="overline mb-3">Últimos movimentos</div>
            <table className="pcp-table">
              <thead><tr><th>Data</th><th>Insumo</th><th>Tipo</th><th style={{ textAlign: "right" }}>Qtd</th><th>OS</th><th>NF</th></tr></thead>
              <tbody>
                {movs.slice(0, 20).map((m) => {
                  const ins = items.find((x) => x.id === m.insumo_id);
                  const os = ordens.find((o) => o.id === m.os_id);
                  return (
                    <tr key={m.id}>
                      <td className="font-mono text-xs">{new Date(m.created_at).toLocaleDateString("pt-BR")}</td>
                      <td>{ins?.nome || "—"}</td>
                      <td>{m.tipo === "entrada" ? <span style={{ color: "#34C759", display: "flex", alignItems: "center", gap: 4 }}><ArrowDown size={14} weight="bold" /> Entrada</span> : <span style={{ color: "#DC2626", display: "flex", alignItems: "center", gap: 4 }}><ArrowUp size={14} weight="bold" /> Saída</span>}</td>
                      <td className="font-mono" style={{ textAlign: "right" }}>{m.quantidade}</td>
                      <td className="font-mono">{os?.numero || "—"}</td>
                      <td>{m.nota_fiscal || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Editar insumo" : "Novo insumo"}>
        <div style={{ display: "grid", gap: 12 }}>
          <F label="Nome *"><input className="input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="insumo-nome" required /></F>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <F label="Unidade"><input className="input" value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} /></F>
            <F label="Estoque mínimo"><input type="number" step="0.01" className="input" value={form.estoque_minimo} onChange={(e) => setForm({ ...form, estoque_minimo: e.target.value })} /></F>
            <F label="Saldo inicial"><input type="number" step="0.01" className="input" value={form.saldo} onChange={(e) => setForm({ ...form, saldo: e.target.value })} /></F>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="btn-save-insumo">Salvar</button>
        </div>
      </Modal>

      <Modal open={!!movModal} onClose={() => setMovModal(null)} title={`Movimentar: ${movModal?.nome || ""}`}>
        <div style={{ display: "grid", gap: 12 }}>
          <F label="Tipo">
            <select className="select" value={mov.tipo} onChange={(e) => setMov({ ...mov, tipo: e.target.value })}>
              <option value="entrada">Entrada</option>
              <option value="saida">Saída</option>
            </select>
          </F>
          <F label="Quantidade"><input type="number" step="0.01" className="input" value={mov.quantidade} onChange={(e) => setMov({ ...mov, quantidade: e.target.value })} data-testid="mov-qtd" /></F>
          {mov.tipo === "saida" && <F label="OS vinculada"><select className="select" value={mov.os_id} onChange={(e) => setMov({ ...mov, os_id: e.target.value })}><option value="">—</option>{ordens.map((o) => <option key={o.id} value={o.id}>{o.numero}</option>)}</select></F>}
          {mov.tipo === "entrada" && <F label="Nota fiscal"><input className="input" value={mov.nota_fiscal} onChange={(e) => setMov({ ...mov, nota_fiscal: e.target.value })} /></F>}
          <F label="Observação"><textarea className="textarea" rows={2} value={mov.observacao} onChange={(e) => setMov({ ...mov, observacao: e.target.value })} /></F>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setMovModal(null)}>Cancelar</button>
          <button className="btn btn-primary" onClick={registerMov} data-testid="btn-save-mov">Registrar</button>
        </div>
      </Modal>
    </>
  );
}
function F({ label, children }) { return <div><div className="label">{label}</div>{children}</div>; }
